import { memo, useCallback, useMemo } from 'react'
import { ArrowRight, CalendarRange, Info, Plus, TriangleAlert, X } from 'lucide-react'
import { isoToParisDateTimeInput, parisDateTimeInputToIso } from '../../lib/paris-date-time.js'
import type { CgOverlayValidityPeriod } from '../../types/index.js'
import { PERIOD_STATUS_LABELS, hasPermanentPeriod, periodStatus, periodsSummary } from './cgOverlayValidity.js'

/** Periode du brouillon : la cle ne sert qu'a React, les dates sont en ISO. */
export interface DraftPeriod extends CgOverlayValidityPeriod {
  key: string
}

export function newDraftPeriod(period: CgOverlayValidityPeriod = { startsAt: null, endsAt: null }): DraftPeriod {
  return { ...period, key: `${Date.now()}-${Math.random().toString(36).slice(2)}` }
}

/** Fin anterieure ou egale au debut : bloque l'enregistrement. */
export function periodInverted(period: CgOverlayValidityPeriod): boolean {
  if (!period.startsAt || !period.endsAt) return false
  return Date.parse(period.endsAt) <= Date.parse(period.startsAt)
}

interface Props {
  periods: DraftPeriod[]
  onChange: (periods: DraftPeriod[]) => void
}

export const CgOverlayValiditySection = memo(function CgOverlayValiditySection({ periods, onChange }: Props) {
  const now = useMemo(() => new Date(), [])
  const permanent = periods.length > 1 && hasPermanentPeriod(periods)

  const patch = useCallback((key: string, field: 'startsAt' | 'endsAt', value: string) => {
    onChange(periods.map(period => period.key === key
      ? { ...period, [field]: value ? parisDateTimeInputToIso(value) || null : null }
      : period))
  }, [onChange, periods])

  const remove = useCallback((key: string) => {
    onChange(periods.filter(period => period.key !== key))
  }, [onChange, periods])

  return (
    <section className="cg-form-section" aria-labelledby="cg-validity-title">
      <div className="cg-section-heading">
        <h3 id="cg-validity-title"><CalendarRange size={14} aria-hidden="true" />Périodes de validité</h3>
        <button type="button" className="cg-button" onClick={() => onChange([...periods, newDraftPeriod()])}>
          <Plus size={13} aria-hidden="true" />Ajouter une période
        </button>
      </div>

      {periods.length > 0 && (
        <ol className="cg-periods" aria-label="Périodes de validité">
          {periods.map((period, index) => {
            const inverted = periodInverted(period)
            const status = periodStatus(period, now)
            const label = `Période ${index + 1}`
            return (
              <li key={period.key} className={`cg-period${inverted ? ' is-invalid' : ''}`}>
                <CalendarRange size={14} aria-hidden="true" className="cg-period__icon" />
                <label className="cg-period__bound">
                  <span>Du</span>
                  <input
                    type="datetime-local"
                    aria-label={`${label} : début`}
                    value={isoToParisDateTimeInput(period.startsAt)}
                    onChange={event => patch(period.key, 'startsAt', event.target.value)}
                  />
                </label>
                <ArrowRight size={13} aria-hidden="true" className="cg-period__arrow" />
                <label className="cg-period__bound">
                  <span>au</span>
                  <input
                    type="datetime-local"
                    aria-label={`${label} : fin`}
                    value={isoToParisDateTimeInput(period.endsAt)}
                    onChange={event => patch(period.key, 'endsAt', event.target.value)}
                  />
                </label>
                <span className={`cg-period-status is-${status.toLowerCase()}`}>
                  <span aria-hidden="true" />{PERIOD_STATUS_LABELS[status]}
                </span>
                <button
                  type="button"
                  className="cg-period__remove"
                  onClick={() => remove(period.key)}
                  aria-label={`Supprimer la ${label.toLowerCase()}`}
                  title="Supprimer la période"
                >
                  <X size={13} aria-hidden="true" />
                </button>
                {inverted && (
                  <span className="cg-period__error" role="alert">
                    <TriangleAlert size={12} aria-hidden="true" />La fin doit suivre le début.
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      )}

      {permanent && (
        <div className="cg-timing-summary is-warning" role="status">
          <TriangleAlert size={13} aria-hidden="true" />
          Une période sans aucune date rend la diffusion permanente : les autres périodes n'ont plus d'effet.
        </div>
      )}
      <p className="cg-timing-summary">
        <Info size={13} aria-hidden="true" />
        {periods.length > 1
          ? 'Les périodes se cumulent : la diffusion part dès qu\'une période couvre le passage. Une date laissée vide ouvre la période.'
          : periods.length === 1
            ? `${periodsSummary(periods, now)} Une date laissée vide ouvre la période.`
            : `${periodsSummary(periods, now)} Ajoutez une période pour la limiter dans le temps.`}
      </p>
    </section>
  )
})
